import { createContext, useContext, useReducer, type ReactNode } from "react";
import {
  SHED_STYLES,
  INITIAL_SHED_DIMENSIONS,
  INITIAL_EXTRAS,
  type ShedStyle,
  type ShedDimensions,
  type DoorState,
  type ExtrasState,
} from "../utils/constants";

interface DoorsState {
  rollUp: DoorState;
  single: DoorState;
  double: DoorState;
  house: DoorState;
}

interface ShedConfigState {
  shedStyle: ShedStyle;
  isAttached: boolean | null;
  dimensions: ShedDimensions;
  hasBlocks: boolean | null;
  doors: DoorsState;
  extras: ExtrasState;
  drawerOpen: boolean;
  configApplied: boolean;
}

type ShedConfigAction =
  | { type: "SET_SHED_STYLE"; style: ShedStyle }
  | { type: "SET_ATTACHED"; value: boolean }
  | { type: "SET_DIMENSION"; field: string; value: number | string }
  | { type: "SET_ALL_DIMENSIONS"; dimensions: Partial<ShedDimensions> }
  | { type: "SET_HAS_BLOCKS"; value: boolean }
  | { type: "TOGGLE_DOOR"; doorType: keyof DoorsState }
  | { type: "SET_DOOR_QUANTITY"; doorType: keyof DoorsState; quantity: number }
  | { type: "SET_DOOR_SIZE"; doorType: keyof DoorsState; size: string }
  | { type: "TOGGLE_EXTRA"; extraType: keyof ExtrasState }
  | { type: "SET_EXTRA_QUANTITY"; extraType: keyof ExtrasState; quantity: number }
  | { type: "SET_EXTRA_SIZE"; extraType: keyof ExtrasState; size: string }
  | { type: "OPEN_DRAWER" }
  | { type: "CLOSE_DRAWER" }
  | { type: "TOGGLE_DRAWER" }
  | { type: "APPLY_CONFIG" }
  | { type: "RESET_CONFIG" };

interface SelectedItem {
  type: string;
  quantity: number;
  size: string;
}

export interface ShedConfigContextValue extends ShedConfigState {
  setShedStyle: (style: ShedStyle) => void;
  setAttached: (value: boolean) => void;
  setDimension: (field: string, value: number | string) => void;
  setAllDimensions: (dimensions: Partial<ShedDimensions>) => void;
  setHasBlocks: (value: boolean) => void;
  toggleDoor: (doorType: keyof DoorsState) => void;
  setDoorQuantity: (doorType: keyof DoorsState, quantity: number) => void;
  setDoorSize: (doorType: keyof DoorsState, size: string) => void;
  toggleExtra: (extraType: keyof ExtrasState) => void;
  setExtraQuantity: (extraType: keyof ExtrasState, quantity: number) => void;
  setExtraSize: (extraType: keyof ExtrasState, size: string) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  applyConfig: () => void;
  resetConfig: () => void;
  getSelectedDoors: () => SelectedItem[];
  getSelectedExtras: () => SelectedItem[];
}

const ShedConfigContext = createContext<ShedConfigContextValue | null>(null);

const initialState: ShedConfigState = {
  shedStyle: SHED_STYLES.GABLE,
  isAttached: null,
  dimensions: { ...INITIAL_SHED_DIMENSIONS },
  hasBlocks: null,
  doors: {
    rollUp: { selected: false, quantity: 1, size: "" },
    single: { selected: false, quantity: 1 },
    double: { selected: false, quantity: 1 },
    house: { selected: false, quantity: 1 },
  },
  extras: { ...INITIAL_EXTRAS },
  drawerOpen: false,
  configApplied: false,
};

function shedConfigReducer(state: ShedConfigState, action: ShedConfigAction): ShedConfigState {
  switch (action.type) {
    case "SET_SHED_STYLE":
      return {
        ...state,
        shedStyle: action.style,
        isAttached: action.style === SHED_STYLES.LEAN_TO ? state.isAttached : null,
      };

    case "SET_ATTACHED":
      return { ...state, isAttached: action.value };

    case "SET_DIMENSION":
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          [action.field]: action.value,
        },
      };

    case "SET_ALL_DIMENSIONS":
      return {
        ...state,
        dimensions: { ...state.dimensions, ...action.dimensions },
      };

    case "SET_HAS_BLOCKS":
      return { ...state, hasBlocks: action.value };

    case "TOGGLE_DOOR":
      return {
        ...state,
        doors: {
          ...state.doors,
          [action.doorType]: {
            ...state.doors[action.doorType],
            selected: !state.doors[action.doorType].selected,
          },
        },
      };

    case "SET_DOOR_QUANTITY":
      return {
        ...state,
        doors: {
          ...state.doors,
          [action.doorType]: {
            ...state.doors[action.doorType],
            quantity: Math.max(1, action.quantity),
          },
        },
      };

    case "SET_DOOR_SIZE":
      return {
        ...state,
        doors: {
          ...state.doors,
          [action.doorType]: {
            ...state.doors[action.doorType],
            size: action.size,
          },
        },
      };

    case "TOGGLE_EXTRA":
      return {
        ...state,
        extras: {
          ...state.extras,
          [action.extraType]: {
            ...state.extras[action.extraType],
            selected: !state.extras[action.extraType].selected,
          },
        },
      };

    case "SET_EXTRA_QUANTITY":
      return {
        ...state,
        extras: {
          ...state.extras,
          [action.extraType]: {
            ...state.extras[action.extraType],
            quantity: Math.max(1, action.quantity),
          },
        },
      };

    case "SET_EXTRA_SIZE":
      return {
        ...state,
        extras: {
          ...state.extras,
          [action.extraType]: {
            ...state.extras[action.extraType],
            size: action.size,
          },
        },
      };

    case "OPEN_DRAWER":
      return { ...state, drawerOpen: true };

    case "CLOSE_DRAWER":
      return { ...state, drawerOpen: false };

    case "TOGGLE_DRAWER":
      return { ...state, drawerOpen: !state.drawerOpen };

    case "APPLY_CONFIG":
      return { ...state, configApplied: true, drawerOpen: false };

    case "RESET_CONFIG":
      return { ...initialState };

    default:
      return state;
  }
}

export function ShedConfigProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(shedConfigReducer, initialState);

  const setShedStyle = (style: ShedStyle) => dispatch({ type: "SET_SHED_STYLE", style });
  const setAttached = (value: boolean) => dispatch({ type: "SET_ATTACHED", value });
  const setDimension = (field: string, value: number | string) =>
    dispatch({ type: "SET_DIMENSION", field, value });
  const setAllDimensions = (dimensions: Partial<ShedDimensions>) =>
    dispatch({ type: "SET_ALL_DIMENSIONS", dimensions });
  const setHasBlocks = (value: boolean) => dispatch({ type: "SET_HAS_BLOCKS", value });
  const toggleDoor = (doorType: keyof DoorsState) => dispatch({ type: "TOGGLE_DOOR", doorType });
  const setDoorQuantity = (doorType: keyof DoorsState, quantity: number) =>
    dispatch({ type: "SET_DOOR_QUANTITY", doorType, quantity });
  const setDoorSize = (doorType: keyof DoorsState, size: string) =>
    dispatch({ type: "SET_DOOR_SIZE", doorType, size });
  const toggleExtra = (extraType: keyof ExtrasState) =>
    dispatch({ type: "TOGGLE_EXTRA", extraType });
  const setExtraQuantity = (extraType: keyof ExtrasState, quantity: number) =>
    dispatch({ type: "SET_EXTRA_QUANTITY", extraType, quantity });
  const setExtraSize = (extraType: keyof ExtrasState, size: string) =>
    dispatch({ type: "SET_EXTRA_SIZE", extraType, size });
  const openDrawer = () => dispatch({ type: "OPEN_DRAWER" });
  const closeDrawer = () => dispatch({ type: "CLOSE_DRAWER" });
  const toggleDrawer = () => dispatch({ type: "TOGGLE_DRAWER" });
  const applyConfig = () => dispatch({ type: "APPLY_CONFIG" });
  const resetConfig = () => dispatch({ type: "RESET_CONFIG" });

  const getSelectedDoors = (): SelectedItem[] => {
    return Object.entries(state.doors)
      .filter(([, door]) => door.selected)
      .map(([type, door]) => ({
        type,
        quantity: door.quantity,
        size: door.size || "",
      }));
  };

  const getSelectedExtras = (): SelectedItem[] => {
    return Object.entries(state.extras)
      .filter(([, extra]) => extra.selected)
      .map(([type, extra]) => ({
        type,
        quantity: extra.quantity,
        size: extra.size || "",
      }));
  };

  const value: ShedConfigContextValue = {
    ...state,
    setShedStyle,
    setAttached,
    setDimension,
    setAllDimensions,
    setHasBlocks,
    toggleDoor,
    setDoorQuantity,
    setDoorSize,
    toggleExtra,
    setExtraQuantity,
    setExtraSize,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    applyConfig,
    resetConfig,
    getSelectedDoors,
    getSelectedExtras,
  };

  return <ShedConfigContext.Provider value={value}>{children}</ShedConfigContext.Provider>;
}

export function useShedConfig(): ShedConfigContextValue {
  const context = useContext(ShedConfigContext);
  if (!context) {
    throw new Error("useShedConfig must be used within a ShedConfigProvider");
  }
  return context;
}

export default ShedConfigContext;
